import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Box, Button, IconButton, LinearProgress, Link, Stack, Tooltip, Typography } from '@mui/material';
import { formatBytes } from '../types/text';

export interface FileListItem {
  key: string;
  name: string;
  size: number;
  /** 0–100 while uploading; null when the remaining work is unknown. */
  progress?: number | null;
  uploading?: boolean;
  error?: string;
  /** A file the record still lists but that is gone for good: no size, no link. */
  unavailable?: boolean;
  /** A short remark after the size, e.g. how long a removed file can still come back. */
  note?: string;
  /** One text button at the end of the row, e.g. Restore. */
  action?: { label: string; run: () => void; disabled?: boolean };
  onOpen?: () => void;
  onRemove?: () => void;
}

/** The rows under every file field and in every read-only file listing. */
export function FileList({ items, disabled }: { items: FileListItem[]; disabled?: boolean }) {
  if (!items.length) return null;
  return (
    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
      {items.map((item) => (
        <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <InsertDriveFileOutlinedIcon fontSize="small" color={item.error ? 'error' : 'action'} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
              {item.onOpen && !item.uploading ? (
                <Link component="button" type="button" variant="body2" noWrap onClick={item.onOpen}
                      sx={{ textAlign: 'left', minWidth: 0 }}>{item.name}</Link>
              ) : (
                <Typography variant="body2" noWrap color={item.unavailable ? 'text.disabled' : undefined}
                            sx={{ minWidth: 0, fontStyle: item.unavailable ? 'italic' : undefined }}>{item.name}</Typography>
              )}
              {!item.unavailable && (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {formatBytes(item.size)}{item.note ? ` · ${item.note}` : ''}
                </Typography>
              )}
            </Stack>
            {item.uploading && (
              <LinearProgress sx={{ mt: 0.25 }} variant={item.progress == null ? 'indeterminate' : 'determinate'}
                              value={item.progress ?? undefined} />
            )}
            {item.error && <Typography variant="caption" color="error">{item.error}</Typography>}
          </Box>
          {item.action && (
            <Button size="small" onClick={item.action.run} disabled={disabled || item.action.disabled}
                    sx={{ flexShrink: 0, minWidth: 0, py: 0 }}>
              {item.action.label}
            </Button>
          )}
          {item.onRemove && (
            <Tooltip title="Remove">
              <span>
                <IconButton size="small" aria-label={`Remove ${item.name}`} disabled={disabled || item.uploading}
                            onClick={item.onRemove}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ))}
    </Stack>
  );
}
